import React, { useState, useEffect } from "react";
import { H2 } from "@leafygreen-ui/typography";
import TextArea from '@leafygreen-ui/text-area';
import Copyable from "@leafygreen-ui/copyable";
import { css } from "@leafygreen-ui/emotion";
import MultiBox from "../components/MultiBox";
import { useAuth0 } from "@auth0/auth0-react";
import { useApi } from "../providers/Api";
import Button from "@leafygreen-ui/button";

export default function Home () {
  const { user } = useAuth0();

  let [markdown, setMarkdown] = useState("");
  let [campaign, setCampaign] = useState("devrel");
  let [source, setSource] = useState("");
  let [medium, setMedium] = useState("");
  let [content, setContent] = useState("");
  let [term, setTerm] = useState(user.nickname);
  let [utmDataOptions, setUtmDataOptions] = useState();
  let [processedMarkdown, setProcessedMarkdown] = useState("");
  let [isProcessing, setIsProcessing] = useState(false);
  let [formValid, setFormValid] = useState(false);

  const { profile } = useApi();

   // Add an effect to scroll to top whenever processed markdown changes
   useEffect(() => {
    if (markdownResultRef.current) {
      markdownResultRef.current.scrollTop = 0;
    }
  }, [processedMarkdown]);

  useEffect(() => {
    setUtmDataOptions(profile.utms);
  }, [profile]);

  // Check if all required fields are filled
  useEffect(() => {
    const isValid = campaign && source && medium && term;
    setFormValid(isValid);
  }, [campaign, source, medium, content, term]);

  const formStyle = css`
    margin: 0px 50px;
    max-width: 50em;
  `

  const copyableInputStyle = css`
    width: 50em;
  `

  const copyableContainerStyle = css`
  margin: 0px 50px;
  width: 50em;
  background-color: white;
  border-radius: 5px;
  padding: 15px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  height: auto; /* Allow height to adjust to content */
`

const markdownResultRef = React.useRef(null);

  const addUtmToUrl = (urlString) => {
    try {
      const urlObj = new URL(urlString.startsWith('http') ? urlString : 'https://' + urlString);
      
      // Check if it's a MongoDB URL
      if (!urlObj.hostname.includes('mongodb.com')) {
        return urlString; // Return original URL if not MongoDB
      }
      
      // Force HTTPS
      urlObj.protocol = 'https';
      
      // Add trailing slash if needed
      if (urlObj.pathname && !urlObj.pathname.endsWith('/')) {
        urlObj.pathname = urlObj.pathname + '/';
      }
      
      // Add UTM parameters
      if (campaign) urlObj.searchParams.append("utm_campaign", campaign);
      if (source) urlObj.searchParams.append("utm_source", source);
      if (medium) urlObj.searchParams.append("utm_medium", medium);
      if (content) urlObj.searchParams.append("utm_content", content);
      if (term) urlObj.searchParams.append("utm_term", term);
      
      return urlObj.toString();
    } catch (e) {
      return urlString; // Return original URL if parsing fails
    }
  };

  

  const processMarkdown = async () => {
    setIsProcessing(true);
    const markdownLinkRegex = /\[([^\]]+)\]\(([^)]+)\)|(?<![=('"])https?:\/\/[^\s'")<>]+/g;
    let processedText = markdown;
    
    // Find all matches first
    const matches = [];
    let match;
    const markdownCopy = markdown;
    
    while ((match = markdownLinkRegex.exec(markdownCopy)) !== null) {
      matches.push({
        ...match,
        index: match.index
      });
    }
    
    // Process in reverse order to avoid position issues
    for (let i = matches.length - 1; i >= 0; i--) {
      const match = matches[i];
      const fullMatch = match[0];
      const startPos = match.index;
      const endPos = startPos + fullMatch.length;
      
      // Check if it's a markdown link [text](url) or a raw URL
      if (match[2]) {
        // It's a markdown link [text](url)
        const linkText = match[1];
        const originalUrl = match[2];
        
        // Only process MongoDB URLs
        if (originalUrl.includes('mongodb.com')) {
          // Add UTM parameters
          const utmUrl = addUtmToUrl(originalUrl);
          
          // Create the new markdown link with UTM parameters
          const newLink = `[${linkText}](${utmUrl})`;
          
          // Replace in the processed text
          processedText = 
            processedText.substring(0, startPos) + 
            newLink + 
            processedText.substring(endPos);
            
          console.log(`Replaced "${fullMatch}" with "${newLink}"`);
        }
      } else {
        // It's a raw URL
        const originalUrl = fullMatch;
        
        // Only process MongoDB URLs
        if (originalUrl.includes('mongodb.com')) {
          // Add UTM parameters
          const utmUrl = addUtmToUrl(originalUrl);
          
          // Replace in the processed text
          processedText = 
            processedText.substring(0, startPos) + 
            utmUrl + 
            processedText.substring(endPos);
            
          console.log(`Replaced "${originalUrl}" with "${utmUrl}"`);
        }
      }
    }
    
    // Update the processed markdown
    setProcessedMarkdown(processedText);
    setIsProcessing(false);
  };

  return (
    <React.Fragment>
      <H2>Markdown Links To Short URLs</H2>

      <p>
        <div>
          Paste your markdown content and set UTM parameters to generate short links for all MongoDB URLs
        </div>
      </p>

      <p>
        <form className={formStyle}>
          <MultiBox
            label="Campaign *"
            description="Should always be set to `devrel` for the DevRel team"
            onChange={value => setCampaign(value)}
            value={campaign}
            possibleValues={utmDataOptions?.campaigns}
          /><br/>
          <MultiBox
            label="Source *"
            description="Focus area this link tracks to"
            onChange={value => setSource(value)}
            value={source}
            possibleValues={utmDataOptions?.sources}
          /><br/>
          <MultiBox
            label="Medium *"
            description="How was the link shared?"
            onChange={value => setMedium(value)}
            value={medium}
            possibleValues={utmDataOptions?.mediums}
          /><br/>
          <MultiBox
            label="Content"
            description="More details to the medium (episode number, video title, conference name)"
            onChange={value => setContent(value)}
            value={content}
            possibleValues={utmDataOptions?.contents}
          /><br/>
          <MultiBox
            label="Term *"
            description="Used to identify who used or created this link"
            onChange={value => setTerm(value)}
            value={term}
            possibleValues={utmDataOptions?.terms}
          /><br/>
          <TextArea
            label="Markdown Content"
            description="Paste your markdown content here"
            placeholder={formValid ? "Your markdown content..." : "Please fill in all required fields marked with * first"}
            onChange={e => setMarkdown(e.target.value)}
            value={markdown}
            rows={10}
            disabled={!formValid}
            state={!formValid ? "error" : undefined}
          /><br/>
          <Button
            variant="primary"
            onClick={processMarkdown}
            disabled={isProcessing || !markdown.trim() || !formValid}
          >
            {isProcessing ? "Processing..." : "Generate Short Links"}
          </Button>
        </form>
      </p>
  
        <div className={copyableContainerStyle}>
        <Copyable className={copyableInputStyle} label="Processed Markdown with Short Links" description="Copy this updated markdown with short links that include UTM parameters">
  {processedMarkdown}
                </Copyable>
        </div>      
    </React.Fragment>
  );
}